import { Box, SelectChangeEvent, Typography, useTheme } from '@mui/material';

import { OrganizationTypeEnum, UserRolesIdsEnum } from '@verantos-dev/core-enums';
import { UserInfo } from '@verantos-dev/data-access-users';
import {
  ApiWorkflowDialog,
  Checkbox,
  ComboBox,
  Dropdown,
  TextField,
} from '@verantos-dev/shared-components';
import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

import { useUsersListCreateEditDialog } from '../hooks/useUsersListCreateEditDialog';
import { useUsersListCreateEditDialogStrings } from '../hooks/useUsersListCreateEditDialogStrings';

export type createEditUserType = 'createUser' | 'editUser';
export type CreateEditUPropsType = {
  dialogType?: createEditUserType;
  userData?: UserInfo;
};

const UsersListCreateEditDialog = (props: CreateEditUPropsType) => {
  const { dialogType = 'createUser', userData } = props;

  const theme = useTheme();

  const {
    firstName,
    lastName,
    email,
    organizationId,

    onChangeFirstName,
    onChangeLastName,
    onChangeEmail,
    onChangeOrganization,
    onChangeRole,
    onChangeRoles, // TODO: cleanup with curation_platform_roles ff

    isApiLoading,
    apiError,
    apiErrorCode,

    emailError,
    validToCreateEdit,
    organizationOptions,
    organizationType,
    displayOrganizationDropdown,

    isOrResearcherChecked,
    isOrDataAdminChecked,
    isSaAdminChecked,

    // TODO: cleanup with curation_platform_roles ff
    roleOptions,
    rolesValue,
    isCurationPlatformRolesEnabled,

    onCancel,
    onSubmit,

    isLoggedInUserSystemAdmin,
  } = useUsersListCreateEditDialog(dialogType, userData);

  const { dialogStrings, apiWorkflowDialogMessages } = useUsersListCreateEditDialogStrings(
    dialogType,
    apiErrorCode
  );
  return (
    <ApiWorkflowDialog
      dialogMessages={apiWorkflowDialogMessages}
      id={'userListCreateDialog'}
      title={dialogStrings.title}
      size="large"
      isValidForSubmit={validToCreateEdit}
      isSubmitting={isApiLoading}
      submittingError={apiError}
      onClose={onCancel}
      onSubmit={onSubmit}
      onRetrySubmit={onSubmit}
      hideDisplayButtonsOnSuccess
    >
      <Box style={{ display: 'flex', flexDirection: 'column', gap: `${theme.spacing(6)}` }}>
        <Typography variant="body2">{dialogStrings.description}</Typography>
        <Box style={{ display: 'flex', flexDirection: 'row', gap: `${theme.spacing(6)}` }}>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              flex: 1,
            }}
          >
            <Typography
              variant="body2"
              color={theme.palette.text.primary}
              sx={{ fontWeight: '600', lineHeight: '24px' }}
            >
              {dialogStrings.userDetailsSection}
            </Typography>
            <TextField
              id="createEdit-first-name-input"
              type="text"
              width={'auto'}
              displayLabel={dialogStrings.firstNameLabel}
              value={firstName || ''}
              placeholder={dialogStrings.firstNamePlaceholder}
              onChange={onChangeFirstName}
              maxCharacters={60}
              maxCharactersReachedMsg={dialogStrings.firstNameMaxLength}
              beepMaxCharacters={true}
              excludeLeadTailSpace={true}
              helperText={' '}
              disabled={isApiLoading}
            />
            <TextField
              id="createEdit-last-name-input"
              type="text"
              width={'auto'}
              displayLabel={dialogStrings.lastNameLabel}
              value={lastName || ''}
              placeholder={dialogStrings.lastNamePlaceholder}
              onChange={onChangeLastName}
              maxCharacters={60}
              maxCharactersReachedMsg={dialogStrings.lastNameMaxLength}
              beepMaxCharacters={true}
              excludeLeadTailSpace={true}
              helperText={' '}
              disabled={isApiLoading}
            />
            <TextField
              id="createEdit-email-input"
              type="email"
              width={'auto'}
              displayLabel={dialogStrings.emailLabel}
              value={email || ''}
              placeholder={dialogStrings.emailPlaceholder}
              onChange={onChangeEmail}
              maxCharacters={320}
              error={emailError}
              helperText={emailError ? dialogStrings.emailInvalidMessage : ' '}
              disabled={isApiLoading}
            />
          </Box>
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              flex: 1,
            }}
          >
            {displayOrganizationDropdown && (
              <>
                <Typography
                  variant="body2"
                  color={theme.palette.text.primary}
                  sx={{ fontWeight: '600', lineHeight: '24px' }}
                >
                  {dialogStrings.organizationSection}
                </Typography>
                <Dropdown
                  id={'createEdit-organizations-dropdown'}
                  label={dialogStrings.organizationLabel}
                  placeholder={dialogStrings.organizationPlaceholder}
                  width={'auto'}
                  options={organizationOptions}
                  onChange={(event) => onChangeOrganization(event as SelectChangeEvent<string>)}
                  value={organizationId}
                  disabled={isApiLoading || dialogType === 'editUser'}
                ></Dropdown>
              </>
            )}

            <Box>
              <Typography
                variant="body2"
                color={theme.palette.text.primary}
                sx={{ fontWeight: '600', lineHeight: '24px', marginBottom: theme.spacing(3) }}
              >
                {dialogStrings.rolesSection}
              </Typography>
              {organizationType === '' && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing(6),
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: `${borderRadius.sm}px`,
                    padding: theme.spacing(6),
                  }}
                >
                  <Typography variant="body1" color={theme.palette.text.secondary}>
                    {dialogStrings.rolesEmpty}
                  </Typography>
                </Box>
              )}
              {organizationType === OrganizationTypeEnum.CUSTOMER &&
                // TODO: cleanup with curation_platform_roles ff
                (isCurationPlatformRolesEnabled ? (
                  <ComboBox
                    id="createEdit-roles-combobox"
                    label={dialogStrings.rolesSection}
                    options={roleOptions}
                    value={rolesValue}
                    onChange={onChangeRoles}
                    multiple
                    showCheckbox
                    disableCloseOnSelect
                    selectedItemsOnTop
                    limitTags={3}
                    width={'auto'}
                    disabled={isApiLoading}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      pl: `${theme.spacing(2)}`,
                      pt: `${theme.spacing(2)}`,
                    }}
                  >
                    <Checkbox
                      id={UserRolesIdsEnum.OR_RESEARCHER}
                      displayLabel={dialogStrings.roleResearcher}
                      showBorderBox={false}
                      checked={isOrResearcherChecked}
                      value={UserRolesIdsEnum.OR_RESEARCHER}
                      onChange={onChangeRole}
                      disabled={isApiLoading}
                    />
                    {isLoggedInUserSystemAdmin && (
                      <Checkbox
                        id={UserRolesIdsEnum.SA_ADMIN}
                        displayLabel={dialogStrings.roleSysAdmin}
                        showBorderBox={false}
                        checked={isSaAdminChecked}
                        value={UserRolesIdsEnum.SA_ADMIN}
                        onChange={onChangeRole}
                        disabled={isApiLoading}
                      />
                    )}
                  </Box>
                ))}
              {organizationType === OrganizationTypeEnum.RESEARCH_PARTNER && (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: `${theme.spacing(6)}`,
                    justifyContent: 'center',
                    alignItems: 'flex-start',
                    flexShrink: 0,
                    alignSelf: 'stretch',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: `${borderRadius.sm}px`,
                    padding: `${theme.spacing(6)}`,
                    marginTop: `${theme.spacing(2)}`,
                  }}
                >
                  {dialogStrings.rolesResearchPartnerNotSupported}
                </Box>
              )}
              {organizationType === OrganizationTypeEnum.VERANTOS &&
                // TODO: cleanup with curation_platform_roles ff
                (isCurationPlatformRolesEnabled ? (
                  <ComboBox
                    id="createEdit-roles-combobox"
                    label={dialogStrings.rolesSection}
                    options={roleOptions}
                    value={rolesValue}
                    onChange={onChangeRoles}
                    multiple
                    showCheckbox
                    disableCloseOnSelect
                    selectedItemsOnTop
                    limitTags={3}
                    width={'auto'}
                    disabled={isApiLoading}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      pl: `${theme.spacing(2)}`,
                      pt: `${theme.spacing(2)}`,
                    }}
                  >
                    <Checkbox
                      id={UserRolesIdsEnum.VRN_DATA_ADMIN}
                      displayLabel={dialogStrings.roleDataAdmin}
                      showBorderBox={false}
                      checked={isOrDataAdminChecked}
                      value={UserRolesIdsEnum.VRN_DATA_ADMIN}
                      onChange={onChangeRole}
                      disabled={isApiLoading}
                    />
                    <Checkbox
                      id={UserRolesIdsEnum.OR_RESEARCHER}
                      displayLabel={dialogStrings.roleResearcher}
                      showBorderBox={false}
                      checked={isOrResearcherChecked}
                      value={UserRolesIdsEnum.OR_RESEARCHER}
                      onChange={onChangeRole}
                      disabled={isApiLoading}
                    />
                    {isLoggedInUserSystemAdmin && (
                      <Checkbox
                        id={UserRolesIdsEnum.SA_ADMIN}
                        displayLabel={dialogStrings.roleSysAdmin}
                        showBorderBox={false}
                        checked={isSaAdminChecked}
                        value={UserRolesIdsEnum.SA_ADMIN}
                        onChange={onChangeRole}
                        disabled={isApiLoading}
                      />
                    )}
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
      </Box>
    </ApiWorkflowDialog>
  );
};

export default UsersListCreateEditDialog;
